import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Search, Loader2, Trash2, ExternalLink, ChevronDown, ChevronUp, Send, Globe, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const AMBER = "#FFD23F";
const STAGES = ["planning", "searching", "synthesizing", "completed"];
const STAGE_LABEL = { queued: "Queued", planning: "Planning", searching: "Searching the web", synthesizing: "Synthesizing", completed: "Completed", failed: "Failed" };

export default function ResearchCenter() {
  const { refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [current, setCurrent] = useState(null);
  const [starting, setStarting] = useState(false);
  const [followup, setFollowup] = useState("");
  const [asking, setAsking] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const pollRef = useRef(null);

  const loadList = () => api.get("/research").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { loadList(); return () => clearInterval(pollRef.current); }, []);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (current && !["completed", "failed"].includes(current.status)) {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/research/${current.research_id}`);
          setCurrent(data);
          if (["completed", "failed"].includes(data.status)) { loadList(); refreshUser(); }
        } catch { /* transient */ }
      }, 2500);
    }
    return () => clearInterval(pollRef.current);
  }, [current?.research_id, current?.status]);

  const start = async () => {
    if (!query.trim()) return toast.error("Enter a research query");
    setStarting(true);
    try {
      const { data } = await api.post("/research", { query });
      setCurrent(data); setQuery(""); refreshUser(); loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start research"); }
    finally { setStarting(false); }
  };

  const open = async (id) => {
    try { const { data } = await api.get(`/research/${id}`); setCurrent(data); setSourcesOpen(false); }
    catch { toast.error("Could not open research"); }
  };

  const remove = async (id) => {
    await api.delete(`/research/${id}`).catch(() => {});
    if (current?.research_id === id) setCurrent(null);
    loadList();
  };

  const ask = async () => {
    if (!followup.trim()) return;
    setAsking(true);
    try {
      const { data } = await api.post(`/research/${current.research_id}/followup`, { question: followup });
      setCurrent((c) => ({ ...c, followups: [...(c.followups || []), data] }));
      setFollowup(""); refreshUser();
    } catch (e) { toast.error(e?.response?.data?.detail || "Follow-up failed"); }
    finally { setAsking(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto kr-page" data-module="research">
      <div>
        <div className="kr-overline" style={{ color: AMBER }}>[ AI RESEARCH ]</div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">AI Research</h1>
        <p className="text-[#9DA3B4] text-sm mt-1.5">Deep research and smart insights — structured runs with a plan, live sources, and a synthesized report.</p>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* History */}
        <div className="lg:col-span-4 kr-card p-4 h-fit">
          <div className="kr-overline mb-3">RESEARCH HISTORY</div>
          <div className="space-y-2">
            {items.map((r) => (
              <div key={r.research_id} className={`p-3 rounded-lg border flex items-start gap-2 ${current?.research_id === r.research_id ? "border-[#FFD23F]/40 bg-[#FFD23F]/5" : "border-white/[0.07] hover:border-white/20"}`}>
                <button onClick={() => open(r.research_id)} className="flex-1 min-w-0 text-left" data-testid={`research-item-${r.research_id}`}>
                  <div className="text-sm truncate">{r.query}</div>
                  <div className="text-[11px] text-white/35 mt-0.5 flex items-center gap-2">
                    <span className="capitalize" style={{ color: r.status === "completed" ? "#00DC82" : r.status === "failed" ? "#F87171" : AMBER }}>{STAGE_LABEL[r.status] || r.status}</span>
                    · {new Date(r.created_at).toLocaleDateString()}
                  </div>
                </button>
                <button onClick={() => remove(r.research_id)} className="text-white/25 hover:text-red-300 shrink-0 p-1" aria-label="Delete research" data-testid={`research-delete-${r.research_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {items.length === 0 && <div className="text-xs text-white/35 py-4 text-center">No research runs yet.</div>}
          </div>
        </div>

        {/* Main */}
        <div className="lg:col-span-8 space-y-4">
          {/* New research */}
          <div className="kr-card p-5">
            <Textarea value={query} onChange={(e) => setQuery(e.target.value)} rows={3}
              placeholder="What should I research? — e.g. Market landscape for AI-powered website builders in 2026"
              className="bg-[#07080B] border-white/10 resize-none" data-testid="research-query-input" />
            <Button onClick={start} disabled={starting} className="mt-3 rounded-xl h-10 px-6 font-semibold text-black border-0" style={{ background: AMBER }} data-testid="research-start-btn">
              {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-2" /> Start research · 10 cr</>}
            </Button>
          </div>

          {current && (
            <div className="kr-card p-5" data-testid="research-run-detail">
              <div className="font-display text-lg">{current.query}</div>

              {/* Stage indicator */}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                {STAGES.map((s, i) => {
                  const idx = STAGES.indexOf(current.status);
                  const active = current.status === s;
                  const done = current.status === "completed" ? true : idx > i;
                  return (
                    <span key={s} className="flex items-center gap-2">
                      <span className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border ${active ? "text-black" : done ? "text-white/70" : "text-white/30"}`}
                        style={{ background: active ? AMBER : "transparent", borderColor: active || done ? `${AMBER}66` : "rgba(255,255,255,0.1)" }}>
                        {STAGE_LABEL[s]}
                      </span>
                      {i < STAGES.length - 1 && <span className="w-4 h-px bg-white/10" />}
                    </span>
                  );
                })}
                {!["completed", "failed"].includes(current.status) && <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: AMBER }} />}
              </div>

              {current.status === "failed" && (
                <div className="mt-4 text-xs text-red-300 border border-red-400/25 rounded-lg p-3" data-testid="research-error">
                  {current.error || "Research failed."} Your credits were refunded.
                </div>
              )}

              {/* Disclosure */}
              {current.live_web === false && (
                <div className="mt-4 flex items-start gap-2.5 text-xs text-white/60 border border-white/10 rounded-lg p-3" data-testid="research-disclosure">
                  <AlertTriangle className="h-4 w-4 shrink-0" style={{ color: AMBER }} />
                  Live web search was unavailable for this run. This report is based on AI knowledge only and has no externally verified sources.
                </div>
              )}

              {/* Plan */}
              {current.plan?.length > 0 && (
                <div className="mt-5">
                  <div className="kr-overline mb-2">RESEARCH PLAN</div>
                  <ul className="space-y-1.5">
                    {current.plan.map((q, i) => (
                      <li key={i} className="text-sm text-white/70 flex gap-2.5"><span className="font-mono text-xs mt-0.5" style={{ color: AMBER }}>{i + 1}.</span> {q}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sources */}
              {current.sources?.length > 0 && (
                <div className="mt-5 border border-white/[0.08] rounded-xl">
                  <button onClick={() => setSourcesOpen((o) => !o)} className="w-full flex items-center gap-2.5 p-3.5 min-h-[44px]" data-testid="research-sources-toggle">
                    <Globe className="h-4 w-4" style={{ color: AMBER }} />
                    <span className="text-sm font-semibold flex-1 text-left">Sources ({current.sources.length})</span>
                    {sourcesOpen ? <ChevronUp className="h-4 w-4 text-white/35" /> : <ChevronDown className="h-4 w-4 text-white/35" />}
                  </button>
                  {sourcesOpen && (
                    <div className="border-t border-white/[0.06] p-3.5 space-y-3 max-h-72 overflow-y-auto">
                      {current.sources.map((s, i) => (
                        <a key={i} href={s.url} target="_blank" rel="noreferrer" className="block group" data-testid={`research-source-${i}`}>
                          <div className="text-sm text-white/85 group-hover:text-white flex items-start gap-2">
                            <span className="font-mono text-xs mt-0.5" style={{ color: AMBER }}>[{i + 1}]</span>
                            <span className="flex-1">{s.title}</span>
                            <ExternalLink className="h-3 w-3 text-white/30 mt-1 shrink-0" />
                          </div>
                          <div className="text-[11px] text-white/35 truncate ml-6">{s.url}</div>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Synthesis */}
              {current.synthesis && (
                <div className="mt-5">
                  <div className="kr-overline mb-2" style={{ color: "#00DC82" }}>REPORT</div>
                  <pre className="whitespace-pre-wrap text-sm leading-relaxed text-white/85 font-body bg-[#07080B] border border-white/[0.07] rounded-xl p-4 max-h-[560px] overflow-y-auto" data-testid="research-synthesis">{current.synthesis}</pre>
                </div>
              )}

              {/* Follow-ups */}
              {current.status === "completed" && (
                <div className="mt-5">
                  {(current.followups || []).map((f, i) => (
                    <div key={i} className="mb-4" data-testid={`research-followup-${i}`}>
                      <div className="text-sm font-semibold text-white/90">↳ {f.question}</div>
                      <pre className="whitespace-pre-wrap text-sm leading-relaxed text-white/75 font-body mt-1.5">{f.answer}</pre>
                    </div>
                  ))}
                  <div className="flex gap-2 sticky bottom-2">
                    <Textarea value={followup} onChange={(e) => setFollowup(e.target.value)} rows={1}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                      placeholder="Ask a follow-up question… · 1 cr" className="bg-[#07080B] border-white/10 resize-none min-h-[44px]" data-testid="research-followup-input" />
                    <Button onClick={ask} disabled={asking} className="h-11 w-11 p-0 rounded-xl text-black border-0 shrink-0" style={{ background: AMBER }} data-testid="research-followup-send">
                      {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
